import "../data/tactics";
import type { TacticAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { WarchestState } from "../types/game-state";
import { getTacticHandler } from "../utils/tactic-registry";
import { hasCoin } from "../utils/zone-utils";

export function validateTactic(
  state: WarchestState,
  action: TacticAction,
): ValidationResult {
  if (state.phase !== "USE_COINS") {
    return { valid: false, error: "Not in USE_COINS phase" };
  }
  if (action.player !== state.activePlayer) {
    return { valid: false, error: "Not your turn" };
  }
  const zones = state.playerZones[action.player];
  if (!hasCoin(zones, action.coinType, "hand")) {
    return { valid: false, error: "Coin not in hand" };
  }
  // Special case: ROYAL_GUARD_PROTECT uses the ROYAL coin to move a deployed Royal Guard
  if (action.coinType === "ROYAL" && action.unitHex) {
    const cell = state.board[action.unitHex];
    if (
      cell &&
      cell.owner === action.player &&
      cell.unitType === "ROYAL_GUARD"
    ) {
      const rgDef = state.config.units.ROYAL_GUARD;
      if (rgDef?.tacticId) {
        const handler = getTacticHandler(rgDef.tacticId);
        if (handler) return handler.validate(state, action);
      }
    }
  }
  if (action.coinType === "ROYAL") {
    return { valid: false, error: "Royal Coin has no tactic" };
  }
  const unitDef = state.config.units[action.coinType];
  if (!unitDef) {
    return { valid: false, error: "Unknown coin type" };
  }
  if (!unitDef.tacticId) {
    return { valid: false, error: "This unit has no tactic" };
  }
  const cell = state.board[action.unitHex];
  if (!cell) {
    return { valid: false, error: "No unit at unit hex" };
  }
  if (cell.owner !== action.player) {
    return { valid: false, error: "Unit at hex is not yours" };
  }
  if (cell.unitType !== action.coinType) {
    return { valid: false, error: "Coin type does not match unit at hex" };
  }
  const handler = getTacticHandler(unitDef.tacticId);
  if (!handler) {
    return {
      valid: false,
      error: "No tactic handler registered for this unit",
    };
  }
  return handler.validate(state, action);
}
